import React from "react";
import styles from "./last-actual-value-and-timestamp-if-json.module.scss";
import { SmallSpinner } from "@src/components/atoms/small-spinner/small-spinner";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { truncateStringAndAddEllipsis } from "@src/cloud-connect/utils/truncate-string-and-add-ellipsis";
import { useI18N } from "@src/language/language-context";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";

const cssClassName = "last-actual-value-and-timestamp-if-string-or-numeric";

interface DataTypeLastKnownValueAndTimestampTypes {
  timestamp: string;
  value: string;
}

interface LastActualValueAndTimestampIfStringOrNumericPropTypes {
  timeseriesDataType: string;
  lastKnownValueAndTimestamp?: DataTypeLastKnownValueAndTimestampTypes | null;
}

// Recursive component to display JSON data
const JsonViewer = ({ data }: { data: string }) => {
  const i18n = useI18N();
  const texts = {
    dataNotDisplayable: i18n("modules.ourNodeDetailsPanel.dataNotDisplayable"),
  };

  // Check if the data is a non-null object or array
  if (typeof data === "object" && data !== null) {
    return (
      <div className={styles[`${cssClassName}__value`]}>
        {texts.dataNotDisplayable}
      </div>
    );
  }

  // For primitive data types, display the value (truncated if needed)
  return (
    <div className={styles[`${cssClassName}__value`]}>
      {truncateStringAndAddEllipsis(data)}
    </div>
  );
};

export const LastActualValueAndTimestampIfJson = ({
  timeseriesDataType,
  lastKnownValueAndTimestamp,
}: LastActualValueAndTimestampIfStringOrNumericPropTypes) => {
  const i18n = useI18N();

  const texts = {
    loading: i18n("modules.ourNodeDetailsPanel.loading"),
    noJsonDataAvailable: i18n(
      "modules.ourNodeDetailsPanel.noJsonDataAvailable",
    ),
    lastKnownJsonValue: i18n("modules.ourNodeDetailsPanel.lastKnownJsonValue"),
  };
  if (
    lastKnownValueAndTimestamp === undefined ||
    timeseriesDataType === undefined
  ) {
    return (
      <div className={styles[cssClassName]}>
        <div className={styles[`${cssClassName}__loading-wrapper`]}>
          <div className={styles[`${cssClassName}__spinner-wrapper`]}>
            <SmallSpinner />
          </div>
          <div className={styles[`${cssClassName}__value`]}>
            {texts.loading}
          </div>
        </div>
      </div>
    );
  }

  if (lastKnownValueAndTimestamp === null || timeseriesDataType !== "json") {
    return (
      <div className={styles[`${cssClassName}__label`]}>
        <div className={styles[`${cssClassName}__value`]}>
          {texts.noJsonDataAvailable}
        </div>
      </div>
    );
  }

  const { timestamp, value } = lastKnownValueAndTimestamp;

  const keysToDisplay = Object.keys(value).slice(0, 5);

  return (
    <div className={styles[cssClassName]}>
      <div className={styles[`${cssClassName}__label`]}>
        <div className={styles[`${cssClassName}__timestamp`]}>
          {texts.lastKnownJsonValue} (
          <TimezoneDate date={timestamp} />)
        </div>
      </div>
      <FlexColumnContainer verticalGap={6} alignItems="flex-start">
        {keysToDisplay.map((key) => (
          <FlexColumnContainer key={key} verticalGap={0}>
            <div className={styles[`${cssClassName}__timestamp`]}>
              {truncateStringAndAddEllipsis(key)}:{" "}
            </div>
            <JsonViewer data={value[key]} />
          </FlexColumnContainer>
        ))}
      </FlexColumnContainer>
    </div>
  );
};
